import { OUTREACH_CHANNELS } from './home-map/map-layout';

/**
 * Alfred on the Outreach tab — the row list and the deeplink rule behind `alfredInSymphonyOutreach`.
 * Pure and import-light so it can be unit-tested in the node-env suite (see orion-web/CLAUDE.md → Tests);
 * `ChannelsOutBody` calls it only on the toggle's ON branch, and its OFF branch is today's list, unchanged.
 * iOS `OutreachAlfred` and Android `OutreachAlfred.kt` mirror this file 1:1.
 *
 * Alfred (the AI phone receptionist) REPLACES the coming-soon Phone row rather than sitting beside it, and
 * moves up to sit with the channels that work: Email → WhatsApp → SMS → Instagram DM → Alfred → iMessage → …
 */
export const ALFRED_OUTREACH_ROW = { slug: 'alfred', label: 'Alfred' } as const;

export type OutreachRow = (typeof OUTREACH_CHANNELS)[number] | typeof ALFRED_OUTREACH_ROW;

/**
 * The ON list: today's list with Phone gone and Alfred seated before iMessage. Fixed, like `OUTREACH_CHANNELS`
 * — the tab picks one of the two by the toggle. Alfred's row is drawn only when the server sent an Alfred
 * connector entry (it does only when its own `alfredSymphonyConnector` gate is open AND its status read
 * succeeded); without one the tab skips the slot rather than guessing (never a Connect over a receptionist
 * that may already be live) — and Phone does not come back: this list has no Phone.
 */
export const OUTREACH_CHANNELS_WITH_ALFRED: readonly OutreachRow[] = OUTREACH_CHANNELS.flatMap((ch): OutreachRow[] => {
  if (ch.slug === 'phone') return [];
  if (ch.slug === 'imessage') return [ALFRED_OUTREACH_ROW, ch];
  return [ch];
});

/** Whether a `channels-out/<slug>` deeplink points at the Alfred row. An old `channels-out/phone` link —
 *  the home map's Phone card still emits it, a stale share may carry it — lands on Alfred, which is what
 *  took Phone's place. */
export function isAlfredOutreachTarget(target: string | undefined): boolean {
  return target === ALFRED_OUTREACH_ROW.slug || target === 'phone';
}

/**
 * Whether a connector row belongs on the Connectors Tools list, and so is counted and drawn as a tool (the
 * hero's "+N", the home map, the home's connector lists). A row the server flags (`connectors-hidden-from-list`)
 * is off everywhere. Alfred is off exactly where this client draws its Outreach row instead, so it shows in
 * one place, Tools or Outreach, never both and never neither; an app build with no Outreach row has no such
 * rule and keeps Alfred on Tools, which is why the server does not hide it. `alfredOnOutreach` is the same
 * `alfredInSymphonyOutreach` read the Outreach tab draws the row from. Mirrored by iOS/Android
 * `OutreachAlfred.listedOnTools`.
 */
export function listedOnTools(row: { toolkit: string; hiddenFromConnectorsList?: boolean }, alfredOnOutreach: boolean): boolean {
  if (row.hiddenFromConnectorsList) return false;
  return !(alfredOnOutreach && row.toolkit === ALFRED_OUTREACH_ROW.slug);
}
